"use client";

import Image from "next/image";
import { useMemo } from "react";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useTryItOn from "@/app/lib/features/tryItOn/tryItOn.hook";
import { Button } from "@/app/components/shadcn/button";
import CountdownTimer from "../../../CountdownTimer";
import useModal from "@/app/lib/features/modal/modal.hook";
import { Loader2 } from "lucide-react";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const ProductHeader = () => {
  const { product, fetchModalContentIfNeeded, isModalLoading } =
    useProductDetail();
  const { createModal } = useModal();
  const { selectClotheImage } = useTryItOn();

  const flashSale = product?.flash_sale;
  const aiButton = product?.ai_button;

  /**
   * 🔥 HOOK ALWAYS CALLED (RULES OF HOOKS FIX)
   */
  const endTimestamp = useMemo(() => {
    if (
      !flashSale ||
      flashSale.is_active !== "active" ||
      !flashSale.ends_at ||
      flashSale.ends_at === "0"
    ) {
      return null;
    }

    const parsed = new Date(flashSale.ends_at.replace(" ", "T")).getTime();

    return Number.isNaN(parsed) ? null : parsed;
  }, [flashSale]);

  const hasFlashSale = Boolean(endTimestamp);

  const handleTryItOn = async () => {
    if (!product?.id || !product?.images?.length || isModalLoading) return;

    trackGeneric("try_it_on");
    selectClotheImage(product.images[0]);

    const success = await fetchModalContentIfNeeded(product.id, "ai");

    if (success) {
      createModal("try-it-on");
    }
  };

  /**
   * 🚨 RETURN EN SON (HOOKS SONRASI)
   */
  if (!product) return null;

  return (
    <>
      {/* 🧾 Title */}
      <h1 className="text-lg sm:text-xl text-[#333] mb-2">
        {product.brand_name && (
          <span className="font-medium">{product.brand_name}</span>
        )}{" "}
        {product.internet_product_description}
      </h1>

      {/* 🏷 Product Code */}
      {product.product_code?.startsWith("P-") && (
        <p className="text-xs text-gray-400 -mt-1 mb-1">
          Ürün Kodu:{" "}
          <span className="font-medium text-gray-500">
            {product.product_code}
          </span>
        </p>
      )}

      {/* 🔥 Flash Sale */}
      {hasFlashSale && endTimestamp && flashSale && (
        <div
          className="text-white p-3 rounded-lg mb-4"
          style={{
            backgroundColor: flashSale.color_hex || "#d52323",
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-sm sm:text-base">
                {flashSale.title || "Flaş İndirim!"}
              </h3>

              {flashSale.subtitle && (
                <p className="text-xs sm:text-sm opacity-90">
                  {flashSale.subtitle}
                </p>
              )}
            </div>

            <CountdownTimer endTime={endTimestamp} size="md" />
          </div>
        </div>
      )}

      {/* 🤖 AI Try-On */}
      {aiButton && product.try_status && (
        <div
          className="rounded-sm transition-all duration-300 ease-in-out border p-2 flex items-center gap-2 sm:gap-3 w-fit"
          style={{
            backgroundColor: `${aiButton.button_background}1a`,
            borderColor: `${aiButton.button_background}33`,
          }}
        >
          <p className="text-xs sm:text-sm flex-1 text-left">
            {aiButton.text ||
              "Satın almadan önce sanal olarak denemek için tıklayın."}
          </p>

          <Button
            size="sm"
            onClick={handleTryItOn}
            disabled={isModalLoading}
            style={{
              backgroundColor: aiButton.button_background,
              color: aiButton.button_text_color,
            }}
            className="hover:opacity-90"
          >
            {isModalLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              aiButton.icon && (
                <div className="relative w-3.5 h-3.5">
                  <Image
                    src={aiButton.icon}
                    alt="Try On"
                    fill
                    className="object-contain"
                    sizes="14px"
                  />
                </div>
              )
            )}
            {aiButton.button_text || "DENE"}
          </Button>
        </div>
      )}
    </>
  );
};

export default ProductHeader;
